import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, RefreshCw, Trash2, Upload, X } from 'lucide-react';
import { authConfig } from '../../config/auth';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmActionModal from '../../components/ConfirmActionModal';
import LoadMoreButton from '../../components/LoadMoreButton';
import { usePaginatedFetch } from '../../hooks/usePaginatedFetch';
import type { SuggestedTrend } from '@crewcast/shared';

const MAX_FILE_SIZE = 100 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/quicktime', 'video/x-m4v'];

interface Company {
  companyId: string;
  displayName: string;
}

function statusBadgeClass(status: string) {
  if (status === 'active') return 'bo-badge bo-badge-green';
  if (status === 'dismissed') return 'bo-badge bo-badge-yellow';
  if (status === 'converted') return 'bo-badge bo-badge-gray';
  return 'bo-badge';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatExpiry(epoch?: number) {
  if (!epoch) return '—';
  return new Date(epoch * 1000).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** YYYY-MM-DD, UTC, matching how the API reads dateFrom/dateTo. */
function todayYmd(): string {
  return new Date().toISOString().slice(0, 10);
}

function monthStartYmd(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

export default function BackOfficeTrendsPage() {
  const { token } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [dateFrom, setDateFrom] = useState(monthStartYmd());
  const [dateTo, setDateTo] = useState(todayYmd());
  // Filters take effect on Apply; the list pages through them 50 at a time
  const [applied, setApplied] = useState({ status: '', companyId: '', dateFrom: monthStartYmd(), dateTo: todayYmd() });
  const params = useMemo(() => {
    const p: Record<string, string> = { dateFrom: applied.dateFrom, dateTo: applied.dateTo };
    if (applied.status) p.status = applied.status;
    if (applied.companyId) p.companyId = applied.companyId;
    return p;
  }, [applied]);
  const { items, loading, loadingMore, hasMore, error, loadMore, refresh } = usePaginatedFetch<SuggestedTrend>({
    url: `${authConfig.apiUrl}/api/backoffice/suggested-trends`,
    token,
    dataKey: 'trends',
    limit: 50,
    params,
  });
  // Deleted rows drop out locally instead of reloading every page loaded so far
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set());
  const trends = items.filter((t) => !deletedIds.has(t.trendId));

  // Create form visibility
  const [formOpen, setFormOpen] = useState(false);

  // Create form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [audience, setAudience] = useState('*');
  const [expiresAt, setExpiresAt] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirm (ConfirmActionModal)
  const [pendingDelete, setPendingDelete] = useState<SuggestedTrend | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    loadCompanies();
  }, []); // eslint-disable-line

  async function loadCompanies() {
    try {
      if (!token) return;
      const res = await fetch(`${authConfig.apiUrl}/api/backoffice/companies?pageSize=200`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) setCompanies(data.companies || []);
    } catch {
      // Non-fatal — companies list is just for the dropdown
    }
  }

  function handleApplyFilters(e: React.FormEvent) {
    e.preventDefault();
    setApplied({ status: filterStatus, companyId: filterCompany, dateFrom, dateTo });
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFormError('Unsupported file type. Use JPG, PNG, GIF, WEBP, MP4, or MOV.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFormError('File exceeds 100 MB limit.');
      return;
    }
    setFormError('');
    setImageFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setImagePreview('');
    }
  }

  function clearFile() {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function uploadAsset(file: File): Promise<string> {
    if (!token) throw new Error('Not authenticated');

    const presignedRes = await fetch(`${authConfig.apiUrl}/api/upload/presigned-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fileName: file.name, fileType: file.type, purpose: 'trend' }),
    });
    const presignedData = await presignedRes.json().catch(() => ({}));
    if (!presignedRes.ok) throw new Error(presignedData.message || 'Failed to get upload URL');

    const { uploadUrl, publicUrl } = presignedData;
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type },
    });
    if (!uploadRes.ok) throw new Error('Failed to upload asset to S3');
    return publicUrl;
  }

  async function handleCreateTrend(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !description.trim()) {
      setFormError('Title and description are required.');
      return;
    }
    if (expiresAt && new Date(expiresAt) <= new Date()) {
      setFormError('Expiry date must be in the future.');
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      let imageUrl: string | undefined;
      if (imageFile) {
        imageUrl = await uploadAsset(imageFile);
      }

      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${authConfig.apiUrl}/api/admin/suggested-trends`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          companyId: audience,
          ...(imageUrl ? { image: imageUrl } : {}),
          ...(expiresAt ? { expiresAt } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Failed to create trend');

      // Reset form and reload
      setTitle('');
      setDescription('');
      setAudience('*');
      setExpiresAt('');
      clearFile();
      setFormOpen(false);
      refresh();
    } catch (err: any) {
      setFormError(err.message || 'Failed to create trend');
    } finally {
      setSubmitting(false);
    }
  }

  function openDelete(trend: SuggestedTrend) {
    setDeleteError(null);
    setPendingDelete(trend);
  }

  function closeDelete() {
    setPendingDelete(null);
    setDeleteError(null);
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    const { trendId } = pendingDelete;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      if (!token) throw new Error('Not authenticated');

      const res = await fetch(`${authConfig.apiUrl}/api/admin/suggested-trends/${trendId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to delete trend');
      }
      setDeletedIds(prev => new Set(prev).add(trendId));
      setPendingDelete(null);
    } catch (err: any) {
      // Keep the modal open so the error shows next to the action
      setDeleteError(err.message || 'Failed to delete trend');
    } finally {
      setDeleteLoading(false);
    }
  }

  function audienceLabel(companyId: string) {
    if (companyId === '*') return 'Global';
    const company = companies.find(c => c.companyId === companyId);
    return company ? company.displayName : companyId;
  }

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="bo-page">
      <div className="bo-page-header">
        <h1 className="bo-page-title">Suggested Trends</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="bo-btn bo-btn-secondary"
            onClick={refresh}
            disabled={loading}
            title="Refresh"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            className="bo-btn bo-btn-primary"
            onClick={() => setFormOpen(v => !v)}
          >
            {formOpen ? <ChevronUp size={14} /> : <Plus size={14} />}
            {formOpen ? 'Cancel' : 'Create Trend'}
          </button>
        </div>
      </div>

      {/* Create form */}
      {formOpen && (
        <div className="bo-table-card" style={{ marginBottom: 24 }}>
          <div className="bo-table-header">
            <span className="bo-table-title">New Trend</span>
          </div>
          <form onSubmit={handleCreateTrend} style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Title <span style={{ color: 'var(--color-error)' }}>*</span>
                </label>
                <input
                  className="bo-filter-input"
                  type="text"
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. World Cup Season"
                  maxLength={120}
                  required
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Audience
                </label>
                <select
                  className="bo-filter-select crewcast-select"
                  value={audience}
                  onChange={e => setAudience(e.target.value)}
                >
                  <option value="*">Global (all companies)</option>
                  {companies.map(c => (
                    <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                Description <span style={{ color: 'var(--color-error)' }}>*</span>
              </label>
              <textarea
                className="bo-filter-input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief prompt to inspire employees..."
                rows={3}
                maxLength={600}
                required
                style={{ resize: 'vertical' }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Image / Video (optional)
                </label>
                {imageFile ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {imagePreview && (
                      <img
                        src={imagePreview}
                        alt="preview"
                        style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-gray-200)' }}
                      />
                    )}
                    <span style={{ fontSize: 13, color: 'var(--color-gray-700)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {imageFile.name}
                    </span>
                    <button type="button" onClick={clearFile} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-500)', padding: 0 }}>
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="bo-btn bo-btn-secondary"
                    onClick={() => fileInputRef.current?.click()}
                    style={{ justifyContent: 'flex-start' }}
                  >
                    <Upload size={14} />
                    Choose file
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/x-m4v"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)' }}>
                  Expiry date (optional)
                </label>
                <input
                  className="bo-filter-input"
                  type="date"
                  value={expiresAt}
                  onChange={e => setExpiresAt(e.target.value)}
                  min={today}
                />
              </div>
            </div>

            {formError && (
              <p style={{ color: 'var(--color-error)', fontSize: 13, margin: 0 }}>{formError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" className="bo-btn bo-btn-secondary" onClick={() => setFormOpen(false)}>
                Cancel
              </button>
              <button type="submit" className="bo-btn bo-btn-primary" disabled={submitting}>
                {submitting ? 'Creating…' : 'Create Trend'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <form className="bo-filters" onSubmit={handleApplyFilters}>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Status</label>
          <select className="bo-filter-select crewcast-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="dismissed">Dismissed</option>
            <option value="converted">Converted</option>
          </select>
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Audience</label>
          <select className="bo-filter-select crewcast-select" value={filterCompany} onChange={e => setFilterCompany(e.target.value)}>
            <option value="">All</option>
            <option value="*">Global</option>
            {companies.map(c => (
              <option key={c.companyId} value={c.companyId}>{c.displayName}</option>
            ))}
          </select>
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">Created from</label>
          <input className="bo-filter-input" type="date" value={dateFrom} max={dateTo} onChange={e => setDateFrom(e.target.value)} />
        </div>
        <div className="bo-filter-group">
          <label className="bo-filter-label">To</label>
          <input className="bo-filter-input" type="date" value={dateTo} min={dateFrom} onChange={e => setDateTo(e.target.value)} />
        </div>
        <div className="bo-filter-actions">
          <button type="submit" className="bo-btn bo-btn-primary">Apply</button>
        </div>
      </form>

      {error && (
        <p style={{ color: 'var(--color-error)', fontSize: 14, margin: '0 0 16px' }}>{error}</p>
      )}

      {/* Trends table */}
      <div className="bo-table-card">
        <div className="bo-table-header">
          <span className="bo-table-title">Trends</span>
          <span className="bo-table-count">{trends.length}{hasMore ? '+' : ''} shown</span>
        </div>
        <div className="bo-table-wrapper">
          <table className="bo-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Audience</th>
                <th>Status</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Asset</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: '32px 0' }}>
                    Loading…
                  </td>
                </tr>
              ) : trends.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', color: 'var(--color-gray-400)', padding: '32px 0' }}>
                    No trends found
                  </td>
                </tr>
              ) : (
                trends.map(trend => (
                  <tr key={trend.trendId}>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--color-gray-900)', marginBottom: 2 }}>
                        {trend.title}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--color-gray-500)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {trend.description}
                      </div>
                    </td>
                    <td>{audienceLabel(trend.companyId)}</td>
                    <td>
                      <span className={statusBadgeClass(trend.status)}>{trend.status}</span>
                    </td>
                    <td>{formatDate(trend.createdAt)}</td>
                    <td>{formatExpiry(trend.expiresAt)}</td>
                    <td>
                      {trend.image ? (
                        <img
                          src={trend.image}
                          alt=""
                          style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid var(--color-gray-200)' }}
                        />
                      ) : (
                        <span style={{ color: 'var(--color-gray-400)', fontSize: 12 }}>None</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="bo-btn bo-btn-secondary"
                        style={{ padding: '6px 8px', color: 'var(--color-gray-500)' }}
                        onClick={() => openDelete(trend)}
                        title="Delete trend"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {pendingDelete && (
        <ConfirmActionModal
          title={`Delete "${pendingDelete.title}"?`}
          confirmLabel="Delete trend"
          danger
          loading={deleteLoading}
          error={deleteError}
          onConfirm={handleDelete}
          onClose={closeDelete}
        >
          <p style={{ margin: 0 }}>
            {pendingDelete.companyId === '*'
              ? 'This global trend will be permanently removed for every company.'
              : `This trend will be permanently removed for ${audienceLabel(pendingDelete.companyId)}.`}{' '}
            This can't be undone.
          </p>
        </ConfirmActionModal>
      )}
    </div>
  );
}
